<template>
<div class="broadcast-general">
  <card-minimal id="description" title="Description">
    <div class="d-f fd-c fd-md-r p-t-3 p-b-2 p-x-1">
      <div class="p-x-3 f-1">
        <p class="card-content-title">
          <strong>Title</strong>
          <span v-if="data.liveshopping">Appears on poster button</span>
        </p>
        <form-input type="text" ref="title" v-model="data.title"  />
        <p class="card-content-title m-t-2">
          <strong>Start time</strong>
        </p>
        <form-input type="datetime-local" :readonly="broadcast && ['liveStarting', 'live', 'liveStopping', 'complete'].includes(broadcast.status.life_cycle_status)" :disabled="broadcast && ['liveStarting', 'live', 'liveStopping', 'complete'].includes(broadcast.status.life_cycle_status)" :value="data.scheduled_start_time" :min="minDate" @input="setStartTime" />
        <template v-if="!broadcast">
          <p class="card-content-title m-t-2">
            <strong>Orientation</strong>
            <span>Which orientation the broadcast will be streamed in</span>
          </p>
          <form-radio class="small" :items="orientationSelection" @input="orientationSelection = $event.value" />
        </template>
        <p class="card-content-title m-t-2">
          <strong>Description</strong>
          <span v-if="data.liveshopping">Appears on poster</span>
        </p>
        <form-textarea class="small m-b-2" :toolbar="['bold', 'ul', 'link']" height="185" v-model="data.description" v-if="done" />
        <template v-if="featuresEnabled.includes('ppv')">
          <p class="card-content-title m-t-2">
            <strong>Tags</strong>
            <span>Automatically links broadcast to collections that has the same tags</span>
          </p>
          <FormTags class="f-w m-b-1" placeholder="Add tags" :existingTags="data.tags" @data="setTags" v-if="data.tags !== null && done"/>
          <template v-if="collections.length > 0">
            <div class="d-f f-wrap ai-c m-b-2" style="line-height: 18px;">
              <p style="color: gray; font-size: 14px; margin-right: 5px;">Collection{{ collections.length > 1 ? 's' : ''}}:</p>
              <!-- eslint-disable-next-line -->
              <router-link v-for="(collection, index) in collections" :key="collection.id" :to="`/collection/${collection.id}`" class="link blue m-r-1">{{ collection.metadata.title }}{{ index < collections.length - 1 ? ',' : ''}}</router-link>
            </div>
          </template>
          <template v-else>
            <p style="color: gray; font-size: 14px; margin-right: 5px;">No collections matched</p>
          </template>
        </template>
      </div>
      <div class="p-x-3 f-1">
        <p class="card-content-title">
          <strong>Poster image</strong>
        </p>
        <media-upload class="m-b-2" @images="setImage" :unsplash="true" :multiple="false" title="Poster image" :overwrite="true" size="h/768" :data="{ force: true, images: broadcast && broadcast.images ? broadcast.images : [] }" />
      </div>
    </div>
    <div class="p-x-4 p-b-4" v-if="broadcast">
      <Button :class="`large ${canUpdate ? '' : 'disable'}`" :loading="updating" @click="update">Update</Button>
    </div>
  </card-minimal>
  <card-minimal id="stream" title="Stream" v-if="broadcast && !['complete', 'abandoned'].includes(broadcast.status.life_cycle_status)" class="m-t-4">
    <div class="d-f fd-c fd-md-r p-t-3 p-b-4 p-x-1">
      <div class="p-x-3 f-1">
        <p class="card-content-title">
          <strong>Stream</strong>
          <span>Which stream input to use while streaming</span>
        </p>
        <form-dropdown placeholder="Choose stream" class="site-style semi" @input="streamSelection = $event" :items="streamSelection" :disabled="['liveStarting', 'live', 'liveStopping', 'complete', 'abandoned'].includes(lifeCycleStatus)" />
        <template v-if="assignableUsers && [1, 6, 15, 16].includes(permission) && featuresEnabled.includes('ppv')">
          <p class="card-content-title m-t-2">
            <strong>Assign Streamer</strong>
            <span>{{ $t('broadcast.assign_user_description') }}</span>
          </p>
          <form-dropdown placeholder="Choose user" class="site-style semi" :items="assignableUsers" @input="assignUser" />
        </template>
      </div>
      <div class="p-x-3 f-1 m-t-2 m-t-md-0">
        <p class="card-content-title">
          <strong>Orientation</strong>
          <span>Which orientation the broadcast will be streamed in</span>
        </p>
        <form-radio class="small" :disabled="['liveStarting', 'live', 'liveStopping', 'complete', 'abandoned'].includes(lifeCycleStatus)" :items="orientationSelection" @input="orientationSelection = $event.value" />
      </div>
    </div>
  </card-minimal>
  <CameraSetup id="camera-setup" v-if="broadcast" />
  <scorebar id="scorebar" v-if="broadcast && broadcast.event_details.scorebar.enable && featuresEnabled.includes('scorebar')" class="m-t-4" />
  <!--<card-minimal title="Group stream" class="m-t-4" v-if="broadcast && broadcast.event_details.multi_camera.enable">
    <div class="p-a-4">
      <draggable tag="ul" v-model="cameras">
        <li v-for="(camera, index) in multipleCameras" :key="index">{{ camera.metadata.title }}</li>
      </draggable>
    </div>
  </card-minimal>-->
  <card-minimal id="features" title="Features" class="m-t-4" v-if="[1, 6, 15, 16].includes(user.permission_id)">
    <div class="features p-a-3">
      <toggle class="group" :value="!data.isPrivate" @change="togglePrivate" text="Visibility" explanation="Show the broadcast on your Streamify Play site" v-if="ppvEnabled && featuresEnabled.includes('ppv')" />
      <toggle class="group" :value="data.ppv" @change="togglePPV" text="Pay Per View" explanation="Sell access to this broadcast" v-if="ppvEnabled && featuresEnabled.includes('ppv')" />
      <toggle class="group" :value="data.liveshopping" @change="toggleShopping" text="Live shopping" explanation="Enable the displaying of products during the broadcast" v-if="liveshoppingEnabled && featuresEnabled.includes('lvs') && featuresEnabled.includes('ppv') && activeSubscription" />
      <toggle class="group" :value="data.chat" @change="toggleChat" text="Chat" explanation="Let the audience chat when the broadcast is live" />
      <toggle class="group" :value="data.qa" @change="toggleQA" text="Questions & Answers" explanation="Let the audience send in questions" />
      <toggle class="group" :value="data.radio" @change="toggleRadio" text="Radio" explanation="Enable an audio-only stream" v-if="featuresEnabled.includes('radio')" />
      <toggle class="group" :value="data.scorebar" @change="toggleScorebar" text="Scorebar" explanation="Enable scorebar in your broadcast with teamnames and score" v-if="featuresEnabled.includes('scorebar')" />
      <!--<toggle class="group" :value="data.group_streams" @change="toggleGroupStreams" text="Group stream" explanation="Enable multiple cameras on your stream" v-if="featuresEnabled.includes('multi_camera')" />-->
    </div>
  </card-minimal>
  <card-minimal id="embed" title="Embed the player" class="m-t-4" v-if="broadcast && data.liveshopping">
    <div class="d-f fd-c fd-md-r">
      <div class="f-1 p-x-3 side-border-right p-a-4">
        <p class="card-content-title">
          <strong>Configure embed</strong>
        </p>
        <Button class="extra-sidepadding f-w" @click="openLVSPreview">Configure & Preview</Button>
        <div class="m-t-2 ta-c">
          <a href="#" class="link blue">See our guide on embedding the player</a>
        </div>
      </div>
      <div class="f-3 p-x-3 p-x-4 p-b-4 p-t-md-4">
        <p class="card-content-title">
          <strong>Quick embed</strong>
          <span>Code to include Live Video Shopping Player on your site</span>
        </p>
        <form-input type="text" ref="title" class="mono" :value="liveshoppingEmbedCode" :readonly="true" :copy="true" />
        <Toggle :text="$t('broadcast.include_javascript')" @change="liveshoppingEmbedCodeWithScript = $event" class="small m-t-1 embed-title" />
      </div>
    </div>
  </card-minimal>

  <card-minimal id="embed" title="Links and embeds" class="m-t-4" v-if="broadcast && !data.liveshopping">
    <div class="d-f fd-c fd-md-r p-y-4 p-x-1">
      <div class="f-1 p-x-3">
        <p class="card-content-title">
          <strong>{{ $t('broadcast.embed') }}</strong>
        </p>
        <form-input type="text" ref="title" class="mono" :value="embedCode" :readonly="true" :copy="true" />
      </div>
      <div class="f-1 p-x-3">
        <p class="card-content-title">
          <strong>{{ $t('broadcast.public_link') }}</strong>
        </p>
        <form-input class="mono" type="text" ref="title" :value="directLink" :readonly="true" :copy="true" />
      </div>
    </div>
  </card-minimal>
</div>
</template>

<script>

// import draggable from 'vuedraggable';
import moment from 'moment';

import MediaUpload from '@/components/MediaUpload.vue';
import Scorebar from '@/components/Broadcast/Scorebar.vue';
import Toggle from '@/components/toggle.vue';
import CameraSetup from '@/components/Broadcast/CameraSetup.vue';

const titleCase = str => str.replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

export default {
  components: {
    // draggable,
    Scorebar,
    MediaUpload,
    Toggle,
    CameraSetup,
  },
  computed: {
    livevideoshoppingDomains() {
      const { domains = [] } = this.$store.getters['Play/lvspConfig'];
      return domains || [''];
    },
    previewAddress() {
      return `https://player.streamify.io/#/ls?id=${this.broadcast.id}&origin=${this.livevideoshoppingDomains[0]}`;
    },
    assignableUsers() {
      // eslint-disable-next-line
      const { users, broadcast, assignKey } = this;
      if (!broadcast || !users || users.filter(u => u.permission_id === 12).length === 0) return [];

      let list = [];
      list.push({
        // eslint-disable-next-line
        selected: broadcast.assigned_to ? false : true,
        value: 0,
        string: this.$t('broadcast.no_assigned'),
      });

      list = list.concat(users.filter(user => user.permission_id === 12).map(user => ({
        selected: user.id === broadcast.assigned_to,
        value: user.id,
        string: user.email,
      })));

      return list;
    },
    /**
     * Return the current list of users
     * @return {?Array}
     */
    users() {
      return this.$store.getters['AccountManager/users'];
    },
    cameras: {
      get() {
        return this.multipleCameras;
      },
      set(streams) {
        this.multipleCameras = streams;
        this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              multi_camera: {
                streams: streams.map(s => s.id),
              },
            },
          },
        });
      },
    },
    /**
     * Toggle orientation
     */
    orientationSelection: {
      /**
       * @returns {Array}
       */
      get() {
        if (this.broadcast) {
          return [
            { value: 'landscape', string: this.$t('orientation.landscape'), description: 'Film using landscape orientation' },
            { value: 'portrait', string: this.$t('orientation.portrait'), description: 'Film using portrait orientation' },
          ].map(item => ({ ...item, selected: item.value === this.broadcast.metadata.orientation }));
        }

        return [
          { value: 'landscape', string: this.$t('orientation.landscape'), description: 'Film using landscape orientation' },
          { value: 'portrait', string: this.$t('orientation.portrait'), description: 'Film using portrait orientation' },
        ].map(item => ({ ...item, selected: item.value === this.data.orientation }));
      },
      /**
       * @param {String} value
       */
      set(value) {
        if (this.broadcast) {
          this.$store.dispatch('Broadcast/update', {
            id: this.broadcast.id,
            data: {
              metadata: {
                orientation: value,
              },
            },
          });
        } else {
          this.data.orientation = value;
        }
      },
    },
    /**
     * List of available streams
     */
    streamSelection: {
      /**
       * @returns {Array}
       */
      get() {
        const {
          broadcast: {
            live_stream_id,
          },
        } = this;

        let useDefault = true;

        const streams = this.$store.getters['Streams/streams'].map((stream) => {
          if (stream.id === live_stream_id) {
            useDefault = false;
          }

          return {
            value: stream.id,
            string: `${stream.metadata.title} (${stream.status})`,
            selected: stream.id === live_stream_id,
            disabled: ['error', 'created'].includes(stream.status),
          };
        });

        if (useDefault) {
          streams.push({
            value: '',
            string: this.$t('broadcast.default_stream'),
            selected: true,
            disabled: true,
          });
        }

        return streams;
      },
      async set({ value: stream_id }) {
        if (this.broadcast.stream.metadata.title === 'Ephemeral Stream') {
          const willStop = await swal({
            title: 'Are you sure?',
            text: 'You cannot change to temporary stream again',
            icon: 'warning',
            buttons: true,
            dangerMode: true,
          });

          if (!willStop) {
            return;
          }
        }

        this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            stream_id,
          },
        });
      },
    },
    videoData() {
      const {
        broadcast: {
          resources,
          id,
        },
        lifeCycleStatus,
        fqdn,
        // controls,
      } = this;

      const source = { src: '', type: '' };

      const [formatP] = resources.filter(resource => resource.role.search(/^video.*p$/) >= 0 && resource.role.replace(/\D/g, '') <= 720).sort((a, b) => b.role.replace(/\D/g, '') - a.role.replace(/\D/g, ''));
      if (formatP) {
        source.src = Object.values(formatP)[0].substring(0, 4) === 'http' ? Object.values(formatP)[0] : `https://${fqdn}/resources/${Object.values(formatP)[0]}`;
        source.type = 'video/mp4';
      }

      const [hls] = resources.filter(resource => (resource.role === 'hls' ? resource : null));
      if (hls) {
        source.src = hls.hls.substring(0, 4) === 'http' ? hls.hls.replace('/live/', '/resources/') : `https://${fqdn}${hls.hls.replace('/live/', '/resources/')}`;
        source.type = 'application/x-mpegURL';
      }

      const [format] = resources.filter(resource => resource.role.search(/^video.*[^p]$/) >= 0 && resource.role.replace(/\D/g, '') <= 720).sort((a, b) => b.role.replace(/\D/g, '') - a.role.replace(/\D/g, ''));
      if (format) {
        source.src = Object.values(format)[0].substring(0, 4) === 'http' ? Object.values(format)[0] : `https://${fqdn}/resources/${Object.values(format)[0]}`;
        source.type = 'video/mp4';
      }

      if (['complete'].indexOf(lifeCycleStatus) >= 0) {
        source.src = `https://${fqdn}.streamify.io/resources/${id}.m3u8`;
        source.type = 'application/x-mpegURL';
      }

      return {
        autoplay: true,
        controls: true,
        fluid: true,
        liveui: true,
        // controlBar: controls, // { fullscreenToggle: false, }
        sources: [source],
      };
    },
    /**
     * Return an embed code for a play-broadcast
     * @returns {String}
     */
    embedCode() {
      if (!this.broadcast) {
        return '';
      }
      const { embedCodeWithTitle, embedCodeWithAutoplay, fqdn } = this;
      return `<style>.sb-embed-container { position: relative; padding: 0 0 56.25% 0; height: 0; } .sb-embed-container iframe { border: 0; position: absolute; top: 0; left: 0; width: 100%; height: 100%; }</style><div class="sb-embed-container"><iframe src="https://${fqdn}.streamify.io/play/${embedCodeWithTitle ? 'broadcast' : 'player'}/${this.broadcast.id}?embed${embedCodeWithAutoplay ? '&autoplay' : ''}" frameborder="0" allowfullscreen></iframe></div>`;
    },
    /**
     * Return an embed code for a lvs-broadcast
     * @returns {String}
     */
    liveshoppingEmbedCode() {
      if (!this.broadcast) {
        return '';
      }
      const {
        broadcast: {
          id,
          metadata: {
            orientation,
          },
        },
        liveshoppingEmbedCodeWithScript,
      } = this;
      // eslint-disable-next-line
      return `${liveshoppingEmbedCodeWithScript ? '<script src="https://cdn.streamify.io/liveshopping.min.js"><\/script>' : ''}<streamify-liveshopping id="${id}" orientation="${orientation}"></streamify-liveshopping>`;
    },
    /**
     * Return a directlink to broadcast on play-site
     * @returns {String}
     */
    directLink() {
      if (!this.broadcast) {
        return '';
      }
      return `https://${this.fqdn}.streamify.io/play/broadcast/${this.broadcast.id}`;
    },
    /**
     * Return stream string
     * @returns {String}
     */
    streamStatus() {
      return this.broadcast.stream ? this.broadcast.stream.status : '';
    },
    /**
     * Return life_cycle_status
     * @returns {String}
     */
    lifeCycleStatus() {
      if (!this.broadcast) {
        return '';
      }

      const {
        broadcast: {
          status: {
            life_cycle_status,
          },
        },
      } = this;

      return life_cycle_status;
    },
    /**
     * Return boolean if the player should be available or not
     * @returns {Boolean}
     */
    playerAvailable() {
      const {
        broadcast: {
          status: {
            life_cycle_status,
          },
        },
        streamStatus,
      } = this;
      if (['complete'].indexOf(life_cycle_status) >= 0) {
        return true;
      }
      if (['liveStarting', 'live', 'liveStopping'].indexOf(life_cycle_status) >= 0 || (streamStatus === 'active')) {
        return true;
      }

      return false;
    },
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Check if all fields are valid to schedule a broadcast
     * @returns {Boolean}
     */
    canSchedule() {
      const {
        data: {
          title,
          scheduled_start_time,
        },
      } = this;

      const trialEnded = this.$store.getters['AccountManager/trialEnded'];
      const subscriptionEnded = this.$store.getters['AccountManager/subscriptionEnded'];

      if (trialEnded || subscriptionEnded) {
        return false;
      }

      if (title === '') {
        return false;
      }

      if (moment(scheduled_start_time).isBefore(moment().add(3, 'minutes'))) {
        return false;
      }

      return true;
    },
    /**
     * Check if all fields are valid to update a broadcast
     * @returns {Boolean}
     */
    canUpdate() {
      const {
        data: {
          title,
        },
      } = this;

      if (title === '') {
        return false;
      }

      return true;
    },
  },
  methods: {
    openLVSPreview() {
      window.open(this.previewAddress, '_blank');
    },
    /**
     * Assign a user to the broadcast
     * @param {Number} userId
     */
    async assignUser({ value: userId }) {
      const assign = await this.$store.dispatch('Broadcast/update', {
        id: this.broadcast.id,
        data: {
          assigned_to: userId,
        },
      });

      this.assignKey += 1;

      if (!assign) {
        swal({ title: this.$t('errors.error_occured'), text: this.$t('broadcast.could_not_assign_user'), icon: 'error' });
      }
    },
    setTags(tags) {
      this.data.tags = tags;

      if (this.data.tags.length > 0) {
        this.getCollections();
      } else {
        this.collections = [];
      }
    },
    /**
     * Get collections
     */
    async getCollections() {
      const {
        data: {
          tags,
        },
      } = this;

      if (tags.length > 0) {
        this.collections = await this.$store.dispatch('Collections/getBroadcastCollections', tags);
        return;
      }

      this.collections = [];
    },
    /**
     * @param {Array} cameras
     */
    setMultipleCameras(cameras) {
      const useCameras = cameras && cameras.length > 0 ? cameras : this.broadcast.event_details.multi_camera.streams || [];
      const streams = this.$store.getters['Streams/streams'];

      this.multipleCameras = useCameras.map(camera => streams.find(s => s.id === camera));
    },
    playerInput(val) {
      this.input = val;
    },
    /**
     * Set image on schedule or broadcast
     * depending on which is not null
     * @param {Array} images
     */
    setImage(images) {
      this.data.images = images.length > 0 ? [images[0].value] : [];
    },
    /**
     * Set scheduled start_time
     * @param {String} start_time
     */
    setStartTime(start_time) {
      this.data.scheduled_start_time = start_time;
    },
    /**
     * Toggle PPV flag
     * @param {Boolean} bool
     */
    async togglePPV(bool) {
      if (!this.featuresEnabled.includes('ppv')) {
        return;
      }

      this.data.ppv = bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            protected: {
              pay_per_view: {
                enabled: bool,
              },
            },
          },
        });
      }
    },
    /**
     * Toggle radio flag
     * @param {Boolean} bool
     */
    async toggleRadio(bool) {
      this.data.radio = bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              radio: {
                enable: bool,
              },
            },
          },
        });
      }
    },
    async toggleScorebar(bool) {
      this.data.scorebar = bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              scorebar: {
                enable: bool,
              },
            },
          },
        });
      }
    },
    /**
     * Toggle group streams flag
     * @param {Boolean} bool
     */
    async toggleGroupStreams(bool) {
      this.data.group_streams = bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              multi_camera: {
                enable: bool,
              },
            },
          },
        });
      }
    },
    /**
     * Toggle qa flag
     * chat flag should be false only if bool is true
     * @param {Boolean} bool
     */
    async toggleQA(bool) {
      this.data.qa = bool;
      if (bool) {
        this.data.chat = false;
      }

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              questions: {
                enable: bool,
              },
              chat: {
                enable: this.data.chat,
              },
            },
          },
        });
      }
    },
    /**
     * Toggle chat flag
     * qa flag should be false only if bool is true
     * @param {Boolean} bool
     */
    async toggleChat(bool) {
      this.data.chat = bool;
      if (bool) {
        this.data.qa = false;
      }

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              chat: {
                enable: bool,
              },
              questions: {
                enable: this.data.qa,
              },
            },
          },
        });
      }
    },
    /**
     * Toggle isPrivate flag
     * @param {Boolean} bool
     */
    async togglePrivate(bool) {
      this.data.isPrivate = !bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            private: !bool,
          },
        });
      }
    },
    /**
     * Toggle liveshopping flag
     * @param {Boolean} bool
     */
    async toggleShopping(bool) {
      if (!this.liveshoppingEnabled || !this.activeSubscription || !this.featuresEnabled.includes('lvs')) {
        return;
      }

      this.data.liveshopping = bool;

      if (this.broadcast) {
        await this.$store.dispatch('Broadcast/update', {
          id: this.broadcast.id,
          data: {
            event_details: {
              shopping: {
                enable: bool,
              },
            },
          },
        });
      }
    },
    /**
     * Schedule a new broadcast with data
     */
    async schedule() {
      if (!this.canSchedule) {
        return;
      }

      const {
        data: {
          title,
          description,
          scheduled_start_time,
          images,
          isPrivate,
          chat,
          qa,
          ppv,
          liveshopping,
          radio,
          tags,
          scorebar,
          orientation,
        },
        liveshoppingEnabled,
        ppvEnabled,
        activeSubscription,
      } = this;

      this.$emit('scheduling', true);

      const broadcast = await this.$store.dispatch('Broadcast/schedule', {
        metadata: {
          title,
          description,
          orientation,
        },
        tags,
        scheduled_start_time: moment(scheduled_start_time).toISOString(),
        images,
        private: isPrivate,
        protected: {
          pay_per_view: {
            enabled: ppv,
          },
        },
        event_details: {
          shopping: {
            enable: (liveshopping && liveshoppingEnabled && activeSubscription) || (liveshoppingEnabled && !ppvEnabled && activeSubscription),
          },
          chat: {
            enable: chat,
          },
          questions: {
            enable: qa,
          },
          radio: {
            enable: radio,
          },
          scorebar: {
            enable: scorebar,
          },
        },
      });

      if (typeof broadcast.id === 'undefined') {
        swal({
          title: 'Error',
          text: 'Something went wrong, please try again',
          type: 'error',
        });
        this.$emit('scheduling', false);
        return;
      }

      this.$emit('scheduled', broadcast.id);
    },
    /**
     * Update a broadcast with data
     */
    async update() {
      if (!this.canUpdate) {
        return;
      }

      const {
        data: {
          title,
          description,
          scheduled_start_time,
          tags,
          images,
        },
      } = this;

      this.updating = true;

      await this.$store.dispatch('Broadcast/update', {
        id: this.broadcast.id,
        data: {
          metadata: {
            title,
            description,
          },
          tags,
          images,
          scheduled_start_time: moment(scheduled_start_time).toISOString(),
        },
      });

      setTimeout(() => {
        this.updating = false;
      }, 1000);
    },
    timeupdate(override = null) {
      if (!this.broadcast) {
        return;
      }

      // eslint-disable-next-line
      if (override === null || isNaN(override)) {
        this.time = '';
        return;
      }

      const {
        lifeCycleStatus,
        broadcast: {
          scheduled_start_time,
          actual_start_time,
        },
      } = this;

      let a = '';
      let b = '';
      let duration = 0;

      if (['ready', 'liveStarting'].includes(lifeCycleStatus)) {
        a = new Date(scheduled_start_time);
        b = new Date();
        duration = moment.duration(Math.abs(a - b), 'millisecond');
      } else if (['live', 'liveEnding'].includes(lifeCycleStatus)) {
        a = new Date();
        b = new Date(actual_start_time);
        duration = moment.duration(Math.abs(a - b), 'millisecond');
      } else {
        duration = moment.duration(override * 1000, 'millisecond');
      }

      const hours = Math.floor(duration.asHours()) >= 10 ? Math.floor(duration.asHours()) : `0${Math.floor(duration.asHours())}`;
      const minutes = Math.floor(duration.minutes()) >= 10 ? Math.floor(duration.minutes()) : `0${Math.floor(duration.minutes())}`;
      const seconds = Math.floor(duration.seconds()) >= 10 ? Math.floor(duration.seconds()) : `0${Math.floor(duration.seconds())}`;
      this.time = `${hours}:${minutes}:${seconds}`;
    },
  },
  data() {
    return {
      data: {
        title: '',
        images: [],
        scheduled_start_time: '',
        description: '',
        ppv: false,
        chat: false,
        qa: false,
        isPrivate: false,
        liveshopping: false,
        radio: false,
        scorebar: false,
        group_streams: false,
        tags: [],
      },
      firstBroadcastFetch: true,
      state: 'loading',
      minDate: moment().format('Y-MM-DDTHH:mm'),
      timeInterval: null,
      time: '',
      updating: false,
      input: 'output',
      embedCodeWithTitle: false,
      embedCodeWithAutoplay: false,
      liveshoppingEmbedCodeWithScript: false,
      multipleCameras: [],
      done: false,
      collections: [],
      assignKey: 0,
    };
  },
  watch: {
    canSchedule: {
      immediate: true,
      handler(bool) {
        this.$emit('canSchedule', bool);
      },
    },
    lifeCycleStatus: {
      immediate: true,
      handler(status) {
        clearInterval(this.timeInterval);
        switch (status) {
          case 'complete':
          case 'liveStopping':
            break;
          default:
            this.timeInterval = setInterval(this.timeupdate, 1000);
            break;
        }
      },
    },
  },
  async mounted() {
    if (this.broadcast) {
      const { broadcast } = this;
      this.data = {
        ...this.data,
        scheduled_start_time: moment(broadcast.scheduled_start_time).format('Y-MM-DDTHH:mm'),
        title: broadcast.metadata.title,
        images: broadcast.images,
        description: broadcast.metadata.description,
        liveshopping: broadcast.event_details.shopping.enable,
        chat: broadcast.event_details.chat.enable,
        qa: broadcast.event_details.questions.enable,
        ppv: broadcast.protected.pay_per_view,
        radio: broadcast.event_details.radio.enable,
        scorebar: broadcast.event_details.scorebar.enable,
        isPrivate: broadcast.private,
        group_streams: broadcast.event_details.multi_camera.enable,
        tags: broadcast.tags,
      };

      await this.$store.dispatch('Streams/getStreams');

      if (this.broadcast.event_details.multi_camera.enable) {
        this.setMultipleCameras();
      }
    } else {
      this.data = {
        ...this.data,
        title: this.$t('library.broadcastname_suggestion', { day: titleCase(moment().format('dddd')) }),
        scheduled_start_time: moment().add(3, 'hours').startOf('hour').format('Y-MM-DDTHH:mm'),
        liveshopping: this.liveshoppingEnabled && this.featuresEnabled.includes('lvs') && !this.featuresEnabled.includes('ppv') && this.activeSubscription,
        chat: this.liveshoppingEnabled && this.featuresEnabled.includes('lvs') && !this.featuresEnabled.includes('ppv') && this.activeSubscription,
        orientation: 'landscape',
        tags: [],
      };

      this.$nextTick(() => {
        try {
          this.$refs.title.$el.querySelector('input').focus();
          this.$refs.title.$el.querySelector('input').select();
        } catch (e) { /* Do nottin' */ }
      });
    }

    this.done = true;

    this.$store.dispatch('Tags/getTags', true);
  },
  beforeDestroy() {
    clearInterval(this.timeInterval);
  },
};
</script>

<style lang="scss">
.broadcast-general {
  .media-upload {
    .card-minimal {
      .card-title {
        display: none;
      }
      .card-content {
        border-radius: 0;
        box-shadow: unset;

        & > .p-a-3 {
          padding: 0;
        }
      }
    }
    .image-gallery {
      .images {
        background-position: 50% 50%;
        background-size: contain;
        background-repeat: no-repeat;
      }
    }
    .upload-area:after, .image-gallery .images {
      padding-bottom: 56.6%;
      padding-top: 0;
    }
  }
}
</style>

<style lang="scss" scoped>
.side-border-right {
  @media (min-width: 768px) {
    border-right: 1px solid #F1F2FA;
  }
}

.broadcast-info {
  align-items: center;
  flex-direction: column;
  justify-content: center;
  display: flex;
  flex: 1;

  .info-title {
    font-size: 15px;
    color: #808080;
    line-height: 32px;
  }

  .time {
    font-size: 72px;
    color: #000000;
    line-height: 80px;
    font-weight: 300;
  }
}

.broadcast-control {
  border-top: 1px solid #EEF0F9;
}

.features {
  display: flex;
  flex-wrap: wrap;

  & > * {
    width: calc(100% - 12px);
    margin: 6px;
  }

  @media (min-width: 768px) {
    & > * {
      width: calc(50% - 12px);
    }
  }

  @media (min-width: 1024px) {
    & > * {
      width: calc(33.3333% - 12px);
    }
  }

  @media (min-width: 1280px) {
    & > * {
      width: calc(25% - 12px);
    }
  }
}
</style>
